import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, filter, firstValueFrom, forkJoin, map, pairwise, startWith, takeUntil } from 'rxjs';
import { EBackendFilterTypes, GenericCrudComponent, IGenericCrudConfiguration } from 'src/app/libs/components/generic-crud/generic-crud.component';
import { DraftableStateTypes, ProductFamily, ProductFormat, Product, Currency, TId } from 'src/app/models';
import { MasterApiService } from 'src/app/services/master-api.service';
import { ProductsAddComponent } from './products-add/products-add.component';
import { ProductsEditComponent } from './products-edit/products-edit.component';
import { ProductsOperativeProductsContextualizerComponent } from './products-operative-products-contextualizer/products-operative-products-contextualizer.component';
import { ProductSubscribersComponent } from './components/product-subscribers/product-subscribers.component';
import { AclService } from 'src/app/libs/services/acl.service';
import { ProductPricesComponent } from './components/product-prices/product-prices.component';
import { ProductStockComponent } from './components/product-stock/product-stock.component';
import { ProductsStorageService } from './product-storage.service';
import { StorageService } from 'src/app/services/storage.service';
import { AlertService } from 'src/app/services/alert.service';
import { ProductProcesSheetsComponent } from './product-proces-sheets/product-proces-sheets.component';
import { ProductSimulationsComponent } from './product-simulations/product-simulations.component';
import {TabRole, TabsService} from "../../../../libs/components/tabs-host/tabs.service";
import {ProductMapComponent} from "./components/product-map/product-map.component";

@Component({
    selector: 'app-products-operative-products',
    templateUrl: './products-operative-products.component.html',
    styleUrls: ['./products-operative-products.component.sass'],
    standalone: false
})
export class ProductsOperativeProductsComponent implements OnDestroy, OnInit {

  private destroy$ = new Subject<void>();

  config?: IGenericCrudConfiguration<Product>;
  productFamilies: ProductFamily[];
  stockFormats?: ProductFormat[];
  isAdmin = this.acl.isAdmin()
  moveFromFamilyVisible = false
  selectedFamily?: TId;
  productMap = ProductMapComponent;

  @ViewChild ('genericCrud') genericCrud?: GenericCrudComponent<Product>;

  constructor(private api: MasterApiService, private translationService: TranslateService, private acl: AclService, private productData: ProductsStorageService, private storage:StorageService, private alertService: AlertService, private tab: TabsService) {
    this.destroy$ = new Subject<void>();
    this.productFamilies = [];

  }
  async ngOnInit(): Promise<void> {
    this.api.auth.user$.pipe(
      takeUntil(this.destroy$),
      filter(u => u !== null && u !== undefined),
      map(u =>  u!.lastCompanyId),
      startWith(null),
      pairwise(),
      filter(([prev, curr]) => prev !== curr),
      map(([, curr]) => curr)
    ).subscribe(async () => {
      this.productFamilies = await firstValueFrom(this.productData.getProductFamilies());
      this.config = {
        name: this.api.resources.product.products.resourceName,
        resource: this.api.resources.product.products.asMappedResource({from: async (data) => {
          const formatCode = await firstValueFrom(this.api.resources.product.productFormats(data.id).get(data.stockFormatId));
          data.stockFormatCode = formatCode.formatCode;
          return {...data} as Product
        }}),
        contextualizer: ProductsOperativeProductsContextualizerComponent,
        editComponent: {
          component: ProductsEditComponent,
          inputPrms: async (row: any) => this.getInputPrms(row),
        },
        createComponent: {
          component: ProductsAddComponent,
          inputPrms: {
            productFamilies: this.productFamilies
          },
        },
        entityFields: {
          code: { isLink: true, dataType: 'code' },
          name: { caption: 'entity.product.product', isLink: true ,  dataType: 'longstring'},
          batchTypeName: { caption: 'entity.product.batchTypeName', dataType: 'shortstring' },
          description: {  caption: 'entity.product.description', dataType: 'longstring', visible: false },
          currentCost: {
            caption: 'entity.product.currentCost',
            dataType: 'number',
            cellTemplate: 'currencyCellTpl'
          },
          standardCostVariance:{
            caption: 'entity.purchaseOrderProducts.costVariance',
            cellTemplate: 'costVarianceTpl',
            dataType: 'number',
          },
          stockFormatName: {
            caption: 'entity.product.stockFormatId',
            dataType: 'code',
            visible: false,
          },
          referenceFormatName: {
            caption: 'entity.product.referenceFormatId',
            dataType: 'code',
            visible: false,
          },
          stateId: {
            dataType: 'code',
            lookup: {
              dataSource: DraftableStateTypes.map(x => ({ id: x.id, name: this.translationService.instant(`enumeration.DraftableStateTypes.${x.name}`) })),
              displayExpr: 'name',
              valueExpr: 'id',
            },
            cellTemplate: 'stateCellTpl',
          },
        },
        changeStateAction: (e) => this.changeState(e),
        stateField: 'stateId',
         genericButtons: [
            { icon: 'move_up', hint: 'entity.product.moveProductsFormFamily', visible: true, disabled: 'this.selection.length <= 0', clickFunction: (e) => this.massiveMoveProductsFromFamily(e)},
            { icon: 'segment', hint: 'enumeration.EHomeValues.products', visible: true, disabled: 'this.selection.length <= 0', clickFunction: (e) => this.goToProductMap(e)}
          ],
        crudButtons: {
          canCreate: true,
          canEdit: true,
          canDelete: true,
          canCopy: true,
        },
      };
    });
  }

  async massiveMoveProductsFromFamily(e: Product) : Promise<void> {
    this.moveFromFamilyVisible = true;

  }

  async goToProductMap(e: Product) : Promise<void> {
   console.log(e)
    const tab = this.tab.createTab(this.productMap, TabRole.EDITION, { inputPrms: {entity: e} });

  }

  async saveMoveFromFamily(): Promise<void> {
    if (!this.selectedFamily) {
      return;
    }
    const products = this.genericCrud?.getDataElementsFromSelection();
    if (products && this.selectedFamily !== undefined) {

      for (const product of products) {
        try {
          await firstValueFrom(this.api.productUpdateFamily(product.id, this.selectedFamily));
        } catch (error) {
          this.alertService.error(error);
        }
      }
      this.genericCrud?.cleanSelection();
      await this.genericCrud?.refreshData();
      this.selectedFamily = undefined;
      this.moveFromFamilyVisible = false;
    }
  }
  getInputPrms(row: any): any {
    const innerEditors: any[] = []
    innerEditors.push({
      name: 'price',
      component: ProductPricesComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name: 'stock',
      component: ProductStockComponent,
      inputPrms: { id: row }
    })
    innerEditors.push({
      name:'processsheets',
      component: ProductProcesSheetsComponent,
      inputPrms: { productId: row }
    })
    innerEditors.push({
      name:'simulations',
      component: ProductSimulationsComponent,
      inputPrms: { id: row }
    })
    if (this.isAdmin) {
      innerEditors.push({
        name: 'subscribers',
        component: ProductSubscribersComponent,
        inputPrms: { id: row },
        isAdminConfig: true
      });
    }
    return {
      productFamilies: this.productFamilies,
      resource: this.api.resources.product.products,
      id: row,
      innerEditors: innerEditors,
    };
  }

  ngOnDestroy(): void {
    this.productData.cleanData();
    this.destroy$.next();
    this.destroy$.complete();
  }

  async changeState(e: { element: Product; destinationStateId: number; }) : Promise<void> {
    if (e.element.stateId !== undefined) {
      await firstValueFrom(this.api.resources.product.products.changeState(e.element.id, e.destinationStateId));
      e.element.stateId = e.destinationStateId;
    }
  }
}
